/**
 * Hotel stay date helpers — nights, active-on-day, confirmation display.
 */

import type { ItineraryItem, TripHotelConfirmation, TripHotelStay } from '../types/client';
import { addDaysYmd } from './wallClock';

export function normalizeConfirmations(
  raw: TripHotelStay['confirmations'] | null | undefined
): TripHotelConfirmation[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      if (typeof c === 'string') {
        const confirmationNumber = c.trim();
        if (!confirmationNumber) return null;
        return { id: crypto.randomUUID(), confirmationNumber };
      }
      const confirmationNumber = String(c.confirmationNumber ?? '').trim();
      if (!confirmationNumber) return null;
      return {
        id: String(c.id || crypto.randomUUID()),
        confirmationNumber,
      };
    })
    .filter((c): c is TripHotelConfirmation => Boolean(c));
}

export function confirmationNumbersLabel(
  confirmations: TripHotelStay['confirmations'] | null | undefined
): string {
  return normalizeConfirmations(confirmations)
    .map((c) => c.confirmationNumber)
    .join(', ');
}

/** Nights between check-in and check-out (exclusive of check-out day). */
export function hotelStayNights(
  checkIn: string | null | undefined,
  checkOut: string | null | undefined
): number {
  const a = (checkIn ?? '').slice(0, 10);
  const b = (checkOut ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b) || b <= a) {
    return 0;
  }
  let nights = 0;
  let current = a;
  let guard = 0;
  while (current < b && guard < 400) {
    nights += 1;
    current = addDaysYmd(current, 1);
    guard += 1;
  }
  return nights;
}

/** True when the stay covers overnight on `dayYmd` (check-in <= day < check-out). */
export function hotelStayIsOvernightOn(
  stay: Pick<TripHotelStay, 'checkInDate' | 'checkOutDate'>,
  dayYmd: string
): boolean {
  const checkIn = (stay.checkInDate ?? '').slice(0, 10);
  const checkOut = (stay.checkOutDate ?? '').slice(0, 10);
  const day = dayYmd.slice(0, 10);
  if (!checkIn || !checkOut || !day) return false;
  return checkIn <= day && day < checkOut;
}

/** Check-in or check-out day (detailed card days on iOS). */
export function hotelStayIsCheckInOrOutOn(
  stay: Pick<TripHotelStay, 'checkInDate' | 'checkOutDate'>,
  dayYmd: string
): boolean {
  const checkIn = (stay.checkInDate ?? '').slice(0, 10);
  const checkOut = (stay.checkOutDate ?? '').slice(0, 10);
  const day = dayYmd.slice(0, 10);
  return day === checkIn || day === checkOut;
}

/** Stays relevant for a day: check-in, check-out, or overnight. */
export function hotelStaysForDate(
  stays: Array<TripHotelStay & { cityVisitId?: string }>,
  dayYmd: string
): Array<TripHotelStay & { cityVisitId?: string }> {
  const day = dayYmd.slice(0, 10);
  return stays
    .filter(
      (s) => hotelStayIsCheckInOrOutOn(s, day) || hotelStayIsOvernightOn(s, day)
    )
    .sort((a, b) => {
      const ai = a.checkInDate ?? '';
      const bi = b.checkInDate ?? '';
      if (ai !== bi) return ai.localeCompare(bi);
      return (a.checkOutDate ?? '').localeCompare(b.checkOutDate ?? '');
    });
}

/**
 * True when an itinerary accommodation item duplicates a HotelStay
 * (same name + overlapping check-in/out dates).
 */
export function accommodationDuplicatesStay(
  item: ItineraryItem,
  stay: TripHotelStay
): boolean {
  if (item.type !== 'accommodation') return false;
  const itemName = (item.title || '').trim().toLowerCase();
  const stayName = (stay.hotelName || '').trim().toLowerCase();
  if (!itemName || !stayName || itemName !== stayName) return false;
  const itemIn = (item.accommodationCheckInDate ?? '').slice(0, 10);
  const itemOut = (item.accommodationCheckOutDate ?? '').slice(0, 10);
  const stayIn = (stay.checkInDate ?? '').slice(0, 10);
  const stayOut = (stay.checkOutDate ?? '').slice(0, 10);
  if (itemIn && stayIn && itemIn !== stayIn) return false;
  if (itemOut && stayOut && itemOut !== stayOut) return false;
  return true;
}

export function newHotelStayId(): string {
  return crypto.randomUUID();
}
